import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {getLanguageEnumByLanguageCode} from '@core/enums/language.enum';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {TranslateService} from '@ngx-translate/core';

@UntilDestroy()
@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-skip-dialog',
    templateUrl: './skip-dialog.component.html',
    styleUrls: ['./skip-dialog.component.scss']
})
export class SkipDialogComponent implements OnInit, OnDestroy {
    @ViewChild('selectItem') selectItem: ElementRef | undefined;
    skipTo: OutlineUnitBasicModel[];
    confirmation = false;
    selectedUnitOrderId = 0;

    audio = new Audio();
    firstPlay = true;
    ttsUrl: string | undefined;
    repeatButtonActive = false;
    ttsIconState = TtsControlEnum.playState;
    textToRead: string;

    constructor(private dialog: MatDialogRef<SkipDialogComponent>, @Inject(MAT_DIALOG_DATA) data: any,
                private ttsHelperService: TtsHelperService, private ttsService: TtsRestService,
                private translate: TranslateService) {
        this.skipTo = data;
    }

    ngOnInit(): void {
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        this.selectedUnitOrderId = this.skipTo[0].unitOrder;
        this.checkIfAudioShouldBePlayed();
    }

    confirm(): void {
        const selectedUnit = this.skipTo.find(unit => unit.unitOrder === this.selectedUnitOrderId);
        if (selectedUnit.loadedWithMore) {
            this.navigateToUnit(this.selectedUnitOrderId);
        } else {
            this.confirmation = true;
            this.firstPlay = true;
            this.audio.addEventListener('ended', this.setButtonOnEnd, false);
            this.checkIfAudioShouldBePlayed();
        }
    }

    selectUnit(orderId: number): void {
        this.selectedUnitOrderId = orderId;
    }

    close(): void {
        this.pauseAudio();
        this.dialog.close();
    }

    navigateToUnit(unitOrderId: number): void {
        this.pauseAudio();
        this.dialog.close({event: unitOrderId});
    }

    onKeyUp(event: KeyboardEvent, unitOrder: number): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.selectUnit(unitOrder);
        }
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.playAudio();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio();
                } else {
                    this.pauseAudio();
                }
            }
        }
    }

    convertTextToSpeech(): void {
        if (!this.confirmation) {
            this.textToRead = this.translate.instant('GENERAL.SKIP_TO') + '. ' + this.translate.instant('CONSENT.SKIP_DIALOG.DESCRIPTION_MULTIPLE')
                + ' ';
            this.skipTo.forEach(unit => {
                this.textToRead += unit.informationUnitTitle + '. ';
            });
        } else {
            this.textToRead = this.translate.instant('CONSENT.SKIP_DIALOG_CONFIRMATION.TITLE') + '. ' +
                this.translate.instant('CONSENT.SKIP_DIALOG_CONFIRMATION.DESCRIPTION');
        }
        const finalText = this.ttsHelperService.removeSSML(this.textToRead);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')),
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonActive = true;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.playAudio();
            }
        });
    }

    playAudio(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    pauseAudio(): void {
        this.ttsIconState = TtsControlEnum.playState;
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            } else {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    checkIfAudioShouldBePlayed(): void {
        if (sessionStorage.getItem('audioMode')) {
            this.pauseAudio();
            this.readText();
        }
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    ngOnDestroy(): void {
        this.pauseAudio();
    }
}
